/**
 * Service for fetching Cursor team spend (per-user, current and previous billing cycle)
 * and merging it into leaderboard entries.
 */

import { LeaderboardEntry } from '../types';
import { getCursorSpendApiUrl } from '../config/amplifyConfig';

export interface TeamMemberSpend {
  userId: number;
  email: string;
  name: string;
  role: string;
  /** On-demand spend only (excludes subscription-included allowance). */
  spendCents: number;
  /** Total cycle spend including included usage — matches Cursor admin dashboard totals per docs. */
  overallSpendCents?: number;
  fastPremiumRequests?: number;
  monthlyLimitDollars?: number | null;
  hardLimitOverrideDollars?: number;
}

/** Total cycle dollars: prefer overall (included + on-demand), else on-demand only. */
export function spendMemberDisplayDollars(m: TeamMemberSpend): number {
  const overall = m.overallSpendCents;
  if (typeof overall === 'number' && Number.isFinite(overall)) {
    return overall / 100;
  }
  return (m.spendCents ?? 0) / 100;
}

/** On-demand-only dollars (overage on top of included allowance). */
export function spendMemberOnDemandDollars(m: TeamMemberSpend): number {
  return (m.spendCents ?? 0) / 100;
}

/**
 * Table/CSV/PDF: total and in brackets on-demand (overage on top of included allowance).
 * When only total is known (no separate on-demand), shows total only.
 */
export function formatSpendDisplay(total?: number, onDemand?: number): string {
  if (total == null || Number.isNaN(total)) return '-';
  const fmt = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (onDemand == null || Number.isNaN(onDemand)) return fmt(total);
  return `${fmt(total)} (${fmt(onDemand)})`;
}

export interface CursorSpendResponse {
  teamMemberSpend: TeamMemberSpend[];
  /** Current billing cycle start in epoch milliseconds (from Cursor API). */
  subscriptionCycleStart?: number;
  totalMembers?: number;
  totalPages?: number;
}

/** Billing cycle date range for display. */
export interface CycleDateRange {
  cycleStart: Date;
  cycleEnd: Date;
  label: string;
}

/**
 * Fetch current billing cycle spend from Cursor API (via proxy).
 * Only current cycle is supported by /teams/spend.
 */
export const fetchCursorSpend = async (): Promise<CursorSpendResponse> => {
  const url = getCursorSpendApiUrl();
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const text = await response.text();
    console.warn('[CURSOR-SPEND] Failed to fetch spend:', response.status, text);
    return { teamMemberSpend: [] };
  }

  const data: CursorSpendResponse = await response.json();
  console.log('[CURSOR-SPEND] Loaded spend for', data.teamMemberSpend?.length ?? 0, 'users');
  return data;
};

/**
 * Get cycle date range from subscriptionCycleStart (epoch ms).
 * Assumes monthly billing; cycle end = start + 1 month.
 */
export const getCycleDateRange = (subscriptionCycleStart?: number): CycleDateRange | null => {
  if (subscriptionCycleStart == null) return null;
  const cycleStart = new Date(subscriptionCycleStart);
  const cycleEnd = new Date(cycleStart);
  cycleEnd.setUTCMonth(cycleEnd.getUTCMonth() + 1);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return {
    cycleStart,
    cycleEnd,
    label: `${fmt(cycleStart)} – ${fmt(cycleEnd)}`,
  };
};

/**
 * Merge current-cycle spend into leaderboard entries by email.
 */
export const mergeSpendIntoLeaderboard = <T extends LeaderboardEntry>(
  entries: T[],
  spendList: TeamMemberSpend[]
): T[] => {
  const byEmail = new Map<string, { total: number; onDemand: number }>();
  spendList.forEach((s) => {
    const email = (s.email || '').toLowerCase().trim();
    if (email) {
      byEmail.set(email, {
        total: spendMemberDisplayDollars(s),
        onDemand: spendMemberOnDemandDollars(s),
      });
    }
  });
  return entries.map((entry) => {
    const email = (entry.email || '').toLowerCase().trim();
    const pair = byEmail.get(email);
    if (!pair) {
      return { ...entry, spendPerUser: undefined, spendOnDemandPerUser: undefined };
    }
    return {
      ...entry,
      spendPerUser: pair.total,
      spendOnDemandPerUser: pair.onDemand,
    };
  });
};

export interface EnrichSpendResult<T extends LeaderboardEntry> {
  entries: T[];
  cycleDateRange: CycleDateRange | null;
}

/**
 * Fetch current cycle spend, merge into entries, and return cycle date (when provided by API).
 */
export const enrichLeaderboardWithSpend = async <T extends LeaderboardEntry>(
  entries: T[]
): Promise<EnrichSpendResult<T>> => {
  const result: EnrichSpendResult<T> = { entries, cycleDateRange: null };
  if (entries.length === 0) return result;
  try {
    const current = await fetchCursorSpend();
    result.cycleDateRange = getCycleDateRange(current.subscriptionCycleStart);
    result.entries = mergeSpendIntoLeaderboard(entries, current.teamMemberSpend ?? []);
  } catch (e) {
    console.warn('[CURSOR-SPEND] Enrich failed:', e);
  }
  return result;
};
